import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Form,
  Input,
  Button,
  Message,
  Spin,
  Alert,
  Card,
  Tag,
  Space,
  Result,
} from '@arco-design/web-react';
import {
  IconUser,
  IconEmail,
  IconPhone,
  IconBook,
  IconCheck,
} from '@arco-design/web-react/icon';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/services/apiClient';
import { API_ENDPOINTS } from '@/config/apiEndpoints';
import PublicLayout from '../registration/PublicLayout';

const FormItem = Form.Item;

function PreRegistrationPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation('public');
  const [form] = Form.useForm();
  const [submitted, setSubmitted] = useState(false);

  // Load pre-registration data
  const { data, isLoading, error } = useQuery({
    queryKey: ['pre-registration', token],
    queryFn: async () => {
      const response = await apiClient.get(
        API_ENDPOINTS.PRE_REGISTRATION_PUBLIC,
        { token }
      );
      return response?.data;
    },
    enabled: !!token,
    retry: 1,
    staleTime: 5 * 60 * 1000,
    onSuccess: (preReg) => {
      if (preReg) {
        form.setFieldsValue({
          full_name: preReg.full_name || '',
          email: preReg.email || '',
          phone: preReg.phone || '',
        });
      }
    },
  });

  // Submit mutation
  const completeMutation = useMutation({
    mutationFn: async (values) => {
      return await apiClient.post(
        API_ENDPOINTS.PRE_REGISTRATION_COMPLETE,
        { token },
        values
      );
    },
    onSuccess: (response) => {
      const result = response?.data;
      if (result?.success) {
        setSubmitted(true);
        Message.success(t('common:messages.success.created'));
      } else {
        Message.error(result?.message || t('common:messages.error.saveError'));
      }
    },
    onError: (err) => {
      const status = err?.response?.status;
      if (status === 410) {
        Message.error(t('common:messages.error.expiredOrCompleted'));
      } else if (status === 409) {
        Message.error(t('common:messages.error.alreadyCompleted'));
      } else {
        Message.error(
          err?.response?.data?.detail || t('common:messages.error.saveError')
        );
      }
    },
  });

  const handleSubmit = (values) => {
    completeMutation.mutate({
      full_name: values.full_name,
      email: values.email,
      phone: values.phone || null,
    });
  };

  // Loading state
  if (isLoading) {
    return (
      <PublicLayout>
        <div
          className="public-card"
          style={{ textAlign: 'center', padding: '60px 40px' }}
        >
          <Spin size={40} />
          <p style={{ marginTop: 16, color: '#86909c' }}>
            {t('preRegistration.loading')}
          </p>
        </div>
      </PublicLayout>
    );
  }

  // Error state (expired, not found, etc.)
  if (error || !data) {
    const status = error?.response?.status;
    let errorTitle = t('preRegistration.notFound');
    let errorMessage = t('preRegistration.notFoundMessage');

    if (status === 410) {
      errorTitle = t('preRegistration.expired');
      errorMessage = t('preRegistration.expiredMessage');
    }

    return (
      <PublicLayout>
        <div className="public-card">
          <Result
            status="warning"
            title={errorTitle}
            subTitle={errorMessage}
            extra={
              <Button type="primary" onClick={() => navigate('/catalogo')}>
                {t('preRegistration.viewCatalog')}
              </Button>
            }
          />
        </div>
      </PublicLayout>
    );
  }

  // Success state (after form submission)
  if (submitted) {
    return (
      <PublicLayout>
        <div className="public-card">
          <Result
            status="success"
            title={t('preRegistration.completed')}
            subTitle={t('preRegistration.completedMessage')}
            extra={
              <Space>
                <Button type="primary" onClick={() => navigate('/catalogo')}>
                  {t('preRegistration.viewCatalog')}
                </Button>
              </Space>
            }
          />
        </div>
      </PublicLayout>
    );
  }

  // Form state
  const program = data.program;

  return (
    <PublicLayout>
      <div className="public-card">
        <div className="public-card-header">
          <h1>{t('preRegistration.title')}</h1>
          <p>
            {t('preRegistration.subtitle')}
          </p>
        </div>

        {/* Program info */}
        {program && (
          <Card
            style={{ marginBottom: 24, background: 'var(--color-fill-1)' }}
            bodyStyle={{ padding: '16px 20px' }}
          >
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Space align="center">
                <IconBook style={{ fontSize: 20, color: 'rgb(var(--primary-6))' }} />
                <span style={{ fontWeight: 600, fontSize: 16 }}>
                  {program.name}
                </span>
              </Space>
              {program.program_type && (
                <Tag color="arcoblue" size="small">
                  {program.program_type}
                </Tag>
              )}
            </Space>
          </Card>
        )}

        <Form
          form={form}
          layout="vertical"
          onSubmit={handleSubmit}
          autoComplete="off"
        >
          <FormItem
            label={t('preRegistration.fullName')}
            field="full_name"
            rules={[
              { required: true, message: t('preRegistration.fullNameRequired') },
              { minLength: 3, message: t('preRegistration.fullNameMin') },
            ]}
          >
            <Input
              prefix={<IconUser />}
              placeholder={t('preRegistration.fullNamePlaceholder')}
              size="large"
            />
          </FormItem>

          <FormItem
            label={t('preRegistration.emailLabel')}
            field="email"
            rules={[
              { required: true, message: t('preRegistration.emailRequired') },
              { type: 'email', message: t('preRegistration.emailInvalid') },
            ]}
          >
            <Input
              prefix={<IconEmail />}
              placeholder={t('preRegistration.emailPlaceholder')}
              size="large"
            />
          </FormItem>

          <FormItem label={t('preRegistration.phoneLabel')} field="phone">
            <Input
              prefix={<IconPhone />}
              placeholder={t('preRegistration.phonePlaceholder')}
              size="large"
            />
          </FormItem>

          <FormItem style={{ marginBottom: 0, marginTop: 32 }}>
            <Button
              type="primary"
              htmlType="submit"
              long
              size="large"
              loading={completeMutation.isPending}
              icon={<IconCheck />}
            >
              {t('preRegistration.submit')}
            </Button>
          </FormItem>
        </Form>
      </div>
    </PublicLayout>
  );
}

export default PreRegistrationPage;
